import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import { startTransition, useActionState, useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { isPlaceContentDirty } from "@/lib/content-dirty";
import { getLibraryReturnHref } from "@/lib/library-search-snapshot";

import {
  deletePlaceSignalAction,
  updatePlaceContent,
  updatePlaceCoverImage,
  updatePlaceInternalNotes,
  updatePlaceStatus,
  uploadPlaceCoverImage,
} from "@/api/places";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { ArticleRichTextEditor } from "@/components/ui/admin/articles/ArticleRichTextEditor";
import { PlaceCoverImageField } from "@/components/ui/admin/places/PlaceCoverImageField";
import { PlaceEditorialChecklist } from "@/components/ui/admin/places/PlaceEditorialChecklist";
import { PlaceEditorialCycleCard } from "@/components/ui/admin/places/PlaceEditorialCycleCard";
import { PlaceEventsPanel } from "@/components/ui/admin/places/PlaceEventsPanel";
import { PlaceOpeningHoursPanel } from "@/components/ui/admin/places/PlaceOpeningHoursPanel";
import { PlaceTaxonomyPanel } from "@/components/ui/admin/places/PlaceTaxonomyPanel";
import { PlaceGoogleSearchField } from "@/components/ui/admin/places/PlaceGoogleSearchField";
import { InternalNotesField } from "@/components/ui/admin/InternalNotesField";
import { ShareLinkButton } from "@/components/ui/admin/ShareLinkButton";
import type { GooglePlaceDetails } from "@/core/services/types/google-places.types";
import type {
  PlaceEditStatusValue,
  PlaceEditor,
  PlaceLinkedEvent,
  PlaceTaxonomy,
  PlaceTypeValue,
  UpdatePlaceState,
} from "@/core/services/types/place.types";
import type { ArtForm, ArtisticPeriod, CulturalArea, ExhibitionType } from "@/core/services/types/taxonomy.types";
import {
  PLACE_TYPE_VALUES,
  buildGoogleMapsPlaceUrl,
  isPlaceEditorialChecklistComplete,
} from "@/core/services/types/place.types";

type PlaceEditTab = "details" | "events";

const initialUpdatePlaceState: UpdatePlaceState = { success: false };

const titleInputClassName =
  "w-full wireframe-border bg-whart-bg px-4 py-3 text-3xl font-bold text-whart-text outline-none placeholder:text-neutral-300 focus:border-whart-dark disabled:opacity-50";

const inputClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

const textareaClassName =
  "w-full resize-y wireframe-border bg-whart-bg px-4 py-3 text-sm leading-relaxed text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

const selectClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

function SaveSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

type PlaceEditQueryData = {
  place: PlaceEditor;
  linkedEvents: PlaceLinkedEvent[];
  placeTaxonomy: PlaceTaxonomy;
  taxonomyData: {
    artForms: ArtForm[];
    artisticPeriods: ArtisticPeriod[];
    culturalAreas: CulturalArea[];
    exhibitionTypes: ExhibitionType[];
  };
};

interface PlaceEditFormProps {
  place: PlaceEditor;
  linkedEvents: PlaceLinkedEvent[];
  shareUrl: string;
  taxonomy: PlaceTaxonomy;
  artForms: ArtForm[];
  artisticPeriods: ArtisticPeriod[];
  culturalAreas: CulturalArea[];
  exhibitionTypes: ExhibitionType[];
}

export function PlaceEditForm({
  place,
  linkedEvents,
  shareUrl,
  taxonomy,
  artForms,
  artisticPeriods,
  culturalAreas,
  exhibitionTypes,
}: PlaceEditFormProps) {
  const t = useTranslations("places.edit");
  const tPlaceTypes = useTranslations("places.library.placeTypes");
  const tCommon = useTranslations("common");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<PlaceEditTab>("details");
  const [contentState, contentFormAction, isContentPending] = useActionState(
    updatePlaceContent,
    initialUpdatePlaceState,
  );
  const [statusState, statusFormAction, isStatusPending] = useActionState(
    updatePlaceStatus,
    initialUpdatePlaceState,
  );
  const [coverImageState, coverImageAction, isCoverImagePending] =
    useActionState(updatePlaceCoverImage, initialUpdatePlaceState);
  const [coverImageUploadState, coverImageUploadAction, isCoverImageUploadPending] =
    useActionState(uploadPlaceCoverImage, initialUpdatePlaceState);
  const [internalNotesState, internalNotesAction, isInternalNotesPending] =
    useActionState(updatePlaceInternalNotes, initialUpdatePlaceState);
  const [pendingStatusTarget, setPendingStatusTarget] =
    useState<PlaceEditStatusValue | null>(null);
  const [name, setName] = useState(place.name);
  const [address, setAddress] = useState(place.address ?? "");
  const [city, setCity] = useState(place.city ?? "");
  const [zipCode, setZipCode] = useState(place.zip_code ?? "");
  const [departmentName, setDepartmentName] = useState(place.department_name ?? "");
  const [websiteUrl, setWebsiteUrl] = useState(place.website_url ?? "");
  const [ticketingUrl, setTicketingUrl] = useState(place.ticketing_url ?? "");
  const [googlePlaceId, setGooglePlaceId] = useState(place.google_place_id ?? "");
  const [googlePlaceLabel, setGooglePlaceLabel] = useState(
    place.google_place_id ? place.name : null,
  );
  const [latitude, setLatitude] = useState(place.latitude);
  const [longitude, setLongitude] = useState(place.longitude);
  const [placeType, setPlaceType] = useState(place.place_type ?? "");
  const [descriptionShort, setDescriptionShort] = useState(
    place.description_short ?? "",
  );
  const [descriptionLong, setDescriptionLong] = useState(
    place.description_long ?? "",
  );
  const [internalNotes, setInternalNotes] = useState(
    () => place.internal_notes ?? "",
  );
  const [linkedEventsResyncCount, setLinkedEventsResyncCount] = useState<
    number | null
  >(null);

  const patchPlaceCache = useCallback(
    (updatedPlace: PlaceEditor) => {
      queryClient.setQueryData<PlaceEditQueryData>(
        ["place-edit", place.id],
        (current) => {
          if (!current) return current;
          return { ...current, place: updatedPlace };
        },
      );
    },
    [place.id, queryClient],
  );

  const savedStatus = place.status;
  const googleMapsQuery = [name, address]
    .map((value) => value.trim())
    .filter(Boolean)
    .join(", ");
  const googleMapsUrl = buildGoogleMapsPlaceUrl(googlePlaceId, googleMapsQuery);
  const checklistPlace = useMemo(
    () => ({
      cover_image_url:
        (coverImageUploadState.success
          ? coverImageUploadState.data.cover_image_url
          : coverImageState.success
            ? coverImageState.data.cover_image_url
            : place.cover_image_url) ?? null,
      name,
      google_place_id: googlePlaceId.trim() || null,
      address: address.trim() || null,
      city: city.trim() || null,
      zip_code: zipCode.trim() || null,
    }),
    [
      coverImageUploadState,
      coverImageState,
      place.cover_image_url,
      name,
      googlePlaceId,
      address,
      city,
      zipCode,
    ],
  );
  const isEditorialChecklistComplete =
    isPlaceEditorialChecklistComplete(checklistPlace);
  const hasUnsavedContentChange = useMemo(
    () =>
      isPlaceContentDirty(place, {
        name,
        address,
        city,
        zipCode,
        departmentName,
        websiteUrl,
        ticketingUrl,
        googlePlaceId,
        latitude,
        longitude,
        placeType,
        descriptionShort,
        descriptionLong,
      }),
    [
      place,
      name,
      address,
      city,
      zipCode,
      departmentName,
      websiteUrl,
      ticketingUrl,
      googlePlaceId,
      latitude,
      longitude,
      placeType,
      descriptionShort,
      descriptionLong,
    ],
  );
  const contentFieldErrors = contentState.success
    ? undefined
    : contentState.fieldErrors;
  const coverImageFieldErrors = coverImageState.success
    ? undefined
    : coverImageState.fieldErrors;
  const isCoverImageBusy = isCoverImagePending || isCoverImageUploadPending;
  const coverImageError = coverImageUploadState.success
    ? undefined
    : coverImageUploadState.error ?? (coverImageState.success ? undefined : coverImageState.error);
  const coverImageSuccess =
    coverImageUploadState.success || coverImageState.success;

  useEffect(() => {
    if (!isStatusPending) {
      setPendingStatusTarget(null);
    }
  }, [isStatusPending]);

  useEffect(() => {
    if (!contentState.success) return;
    const data = contentState.data;
    patchPlaceCache(data);
    setName(data.name);
    setAddress(data.address ?? "");
    setCity(data.city ?? "");
    setZipCode(data.zip_code ?? "");
    setDepartmentName(data.department_name ?? "");
    setWebsiteUrl(data.website_url ?? "");
    setTicketingUrl(data.ticketing_url ?? "");
    setGooglePlaceId(data.google_place_id ?? "");
    setGooglePlaceLabel(data.google_place_id ? data.name : null);
    setLatitude(data.latitude);
    setLongitude(data.longitude);
    setPlaceType(data.place_type ?? "");
    setDescriptionShort(data.description_short ?? "");
    setDescriptionLong(data.description_long ?? "");
    if (contentState.googlePlaceIdChanged) {
      setLinkedEventsResyncCount(contentState.syncedLinkedEventsCount ?? 0);
    }
  }, [contentState.success, contentState, patchPlaceCache]);

  useEffect(() => {
    if (!statusState.success) return;
    patchPlaceCache(statusState.data);
  }, [statusState.success, statusState, patchPlaceCache]);

  useEffect(() => {
    if (!coverImageState.success) return;
    patchPlaceCache(coverImageState.data);
  }, [coverImageState.success, coverImageState, patchPlaceCache]);

  useEffect(() => {
    if (!coverImageUploadState.success) return;
    patchPlaceCache(coverImageUploadState.data);
  }, [coverImageUploadState.success, coverImageUploadState, patchPlaceCache]);

  useEffect(() => {
    if (!internalNotesState.success) return;
    patchPlaceCache(internalNotesState.data);
    setInternalNotes(internalNotesState.data.internal_notes ?? "");
  }, [internalNotesState.success, internalNotesState, patchPlaceCache]);

  function handleStatusAction(targetStatus: PlaceEditStatusValue) {
    setPendingStatusTarget(targetStatus);

    const formData = new FormData();
    formData.set("id", place.id);
    formData.set("status", targetStatus);
    startTransition(() => {
      statusFormAction(formData);
    });
  }

  async function handleDeleteSignal() {
    await deletePlaceSignalAction(place.id);
    navigate(getLibraryReturnHref("places"));
  }

  function submitCoverImageRemoval() {
    const formData = new FormData();
    formData.set("id", place.id);
    formData.set("cover_image_url", "");
    startTransition(() => {
      coverImageAction(formData);
    });
  }

  function handleCoverImageUpload(file: File) {
    const formData = new FormData();
    formData.set("id", place.id);
    formData.set("cover_image", file);
    startTransition(() => {
      coverImageUploadAction(formData);
    });
  }

  function handleInternalNotesBlur() {
    const savedNotes = place.internal_notes ?? "";
    if (internalNotes === savedNotes) return;

    const formData = new FormData();
    formData.set("id", place.id);
    formData.set("internal_notes", internalNotes);
    startTransition(() => {
      internalNotesAction(formData);
    });
  }

  function handleGooglePlaceSelect(details: GooglePlaceDetails) {
    setGooglePlaceId(details.placeId);
    setGooglePlaceLabel(details.name);
    setName(details.name);
    setAddress(details.address ?? "");
    setCity(details.city ?? "");
    setZipCode(details.zipCode ?? "");
    setDepartmentName(details.departmentName ?? "");
    if (details.websiteUrl) {
      setWebsiteUrl(details.websiteUrl);
    }
    setLatitude(details.latitude);
    setLongitude(details.longitude);
  }

  function handleGooglePlaceClear() {
    setGooglePlaceId("");
    setGooglePlaceLabel(null);
    setDepartmentName("");
    setLatitude(0);
    setLongitude(0);
  }

  return (
    <div className="space-y-6">
      <nav aria-label={t("tabsNav")} className="border-b border-black/10">
        <ul className="flex items-center gap-1">
          <li>
            <button
              type="button"
              onClick={() => setActiveTab("details")}
              className={`inline-flex border-b-2 px-3 py-3 text-sm transition-colors ${
                activeTab === "details"
                  ? "border-whart-dark font-medium text-whart-text"
                  : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
              }`}
              aria-current={activeTab === "details" ? "page" : undefined}
            >
              {t("tabs.details")}
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => setActiveTab("events")}
              className={`inline-flex border-b-2 px-3 py-3 text-sm transition-colors ${
                activeTab === "events"
                  ? "border-whart-dark font-medium text-whart-text"
                  : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
              }`}
              aria-current={activeTab === "events" ? "page" : undefined}
            >
              {t("tabs.events")}
              <span className="ml-1.5 text-neutral-400">
                ({linkedEvents.length})
              </span>
            </button>
          </li>
        </ul>
      </nav>

      {activeTab === "events" ? (
        <PlaceEventsPanel events={linkedEvents} />
      ) : (
        <div className="space-y-8 md:grid md:grid-cols-3 md:gap-8 md:space-y-0">
          <form
            action={contentFormAction}
            className="space-y-8 md:col-span-2"
          >
            <input type="hidden" name="id" value={place.id} />
            <input type="hidden" name="google_place_id" value={googlePlaceId} />
            <input type="hidden" name="department_name" value={departmentName} />
            <input type="hidden" name="latitude" value={latitude} />
            <input type="hidden" name="longitude" value={longitude} />

            <div className="flex flex-col items-end gap-2">
              {!contentState.success && contentState.error ? (
                <p
                  role="alert"
                  className="w-full wireframe-border px-3 py-2 text-sm text-whart-urgent"
                >
                  {contentState.error}
                </p>
              ) : null}

              {contentState.success ? (
                <p
                  role="status"
                  className="w-full wireframe-border px-3 py-2 text-sm text-emerald-700"
                >
                  {t("saveContentSuccess")}
                </p>
              ) : null}

              <Button
                type="submit"
                disabled={isContentPending || !hasUnsavedContentChange}
                aria-busy={isContentPending}
                className="inline-flex shrink-0 items-center justify-center gap-2"
              >
                {isContentPending ? <SaveSpinner /> : null}
                {isContentPending ? tCommon("saving") : t("saveContent")}
              </Button>
            </div>

            <PlaceCoverImageField
              savedUrl={place.cover_image_url}
              disabled={isCoverImageBusy}
              isPending={isCoverImageBusy}
              saveSpinner={<SaveSpinner />}
              fieldError={coverImageFieldErrors?.cover_image_url}
              error={coverImageError}
              success={coverImageSuccess}
              onUpload={handleCoverImageUpload}
              onRemove={submitCoverImageRemoval}
            />

            <section className="space-y-2">
              <FieldLabel htmlFor="place-name">
                {t("nameLabel")}
              </FieldLabel>
              <input
                id="place-name"
                name="name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t("namePlaceholder")}
                disabled={isContentPending}
                className={titleInputClassName}
              />
              <FieldError message={contentFieldErrors?.name} />
            </section>

            <PlaceGoogleSearchField
              required
              disabled={isContentPending}
              googlePlaceId={googlePlaceId}
              selectedLabel={googlePlaceLabel}
              mapsUrl={googleMapsUrl}
              fieldError={contentFieldErrors?.google_place_id}
              onSelect={handleGooglePlaceSelect}
              onClear={handleGooglePlaceClear}
            />

            <section className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <label
                  htmlFor="place-address"
                  className="text-xs font-medium uppercase tracking-wider text-neutral-500"
                >
                  {t("addressLabel")}
                </label>
                <input
                  id="place-address"
                  name="address"
                  type="text"
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  placeholder={t("addressPlaceholder")}
                  disabled={isContentPending}
                  className={inputClassName}
                />
                <FieldError message={contentFieldErrors?.address} />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="place-city"
                  className="text-xs font-medium uppercase tracking-wider text-neutral-500"
                >
                  {t("cityLabel")}
                </label>
                <input
                  id="place-city"
                  name="city"
                  type="text"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  placeholder={t("cityPlaceholder")}
                  disabled={isContentPending}
                  className={inputClassName}
                />
                <FieldError message={contentFieldErrors?.city} />
              </div>
              <div className="space-y-2">
                <FieldLabel htmlFor="place-zip-code" required>
                  {t("zipCodeLabel")}
                </FieldLabel>
                <input
                  id="place-zip-code"
                  name="zip_code"
                  type="text"
                  inputMode="numeric"
                  value={zipCode}
                  onChange={(event) => setZipCode(event.target.value)}
                  placeholder={t("zipCodePlaceholder")}
                  disabled={isContentPending}
                  className={inputClassName}
                />
                <FieldError message={contentFieldErrors?.zip_code} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <label
                  htmlFor="place-website"
                  className="text-xs font-medium uppercase tracking-wider text-neutral-500"
                >
                  {t("websiteLabel")}
                </label>
                <input
                  id="place-website"
                  name="website_url"
                  type="url"
                  value={websiteUrl}
                  onChange={(event) => setWebsiteUrl(event.target.value)}
                  placeholder={t("websitePlaceholder")}
                  disabled={isContentPending}
                  className={inputClassName}
                />
                <FieldError message={contentFieldErrors?.website_url} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <label
                  htmlFor="place-ticketing-url"
                  className="text-xs font-medium uppercase tracking-wider text-neutral-500"
                >
                  {t("ticketingUrlLabel")}
                </label>
                <input
                  id="place-ticketing-url"
                  name="ticketing_url"
                  type="url"
                  value={ticketingUrl}
                  onChange={(event) => setTicketingUrl(event.target.value)}
                  placeholder={t("ticketingUrlPlaceholder")}
                  disabled={isContentPending}
                  className={inputClassName}
                />
                <FieldError message={contentFieldErrors?.ticketing_url} />
              </div>
            </section>

            <section className="space-y-2">
              <label
                htmlFor="place-type"
                className="text-xs font-medium uppercase tracking-wider text-neutral-500"
              >
                {t("typeLabel")}
              </label>
              <select
                id="place-type"
                name="place_type"
                value={placeType}
                onChange={(event) =>
                  setPlaceType(event.target.value as PlaceTypeValue | "")
                }
                disabled={isContentPending}
                className={selectClassName}
              >
                <option value="">{t("typeUnselected")}</option>
                {PLACE_TYPE_VALUES.map((type) => (
                  <option key={type} value={type}>
                    {tPlaceTypes(type)}
                  </option>
                ))}
              </select>
              <FieldError message={contentFieldErrors?.place_type} />
            </section>

            <section className="space-y-2">
              <label
                htmlFor="place-description-short"
                className="text-xs font-medium uppercase tracking-wider text-neutral-500"
              >
                {t("abstractLabel")}
              </label>
              <textarea
                id="place-description-short"
                name="description_short"
                rows={4}
                value={descriptionShort}
                onChange={(event) => setDescriptionShort(event.target.value)}
                placeholder={t("abstractPlaceholder")}
                disabled={isContentPending}
                className={textareaClassName}
              />
              <FieldError message={contentFieldErrors?.description_short} />
            </section>

            <section className="space-y-2" aria-labelledby="place-content-label">
              <label
                id="place-content-label"
                className="text-xs font-medium uppercase tracking-wider text-neutral-500"
              >
                {t("contentLabel")}
              </label>
              <ArticleRichTextEditor
                editorKey={place.id}
                value={descriptionLong}
                placeholder={t("contentPlaceholder")}
                disabled={isContentPending}
                labels={{
                  toolbar: t("editor.toolbar"),
                  bold: t("editor.bold"),
                  italic: t("editor.italic"),
                  underline: t("editor.underline"),
                  heading2: t("editor.heading2"),
                  heading3: t("editor.heading3"),
                  bulletList: t("editor.bulletList"),
                  orderedList: t("editor.orderedList"),
                  blockquote: t("editor.blockquote"),
                }}
                onChange={setDescriptionLong}
              />
              <input type="hidden" name="description_long" value={descriptionLong} />
              <FieldError message={contentFieldErrors?.description_long} />
            </section>
          </form>

          <aside className="mt-8 space-y-4 md:col-span-1 md:mt-0">
            <div className="sticky top-6 space-y-4">
              <PlaceEditorialChecklist place={checklistPlace} />

              <div className="wireframe-border bg-white p-5">
                <PlaceEditorialCycleCard
                  savedStatus={savedStatus}
                  itemLabel={place.name}
                  isStatusPending={isStatusPending}
                  pendingStatusTarget={pendingStatusTarget}
                  canPublish={isAdmin}
                  canDelete={isAdmin}
                  publishDisabled={!isEditorialChecklistComplete}
                  onStatusAction={handleStatusAction}
                  onDeleteSignal={handleDeleteSignal}
                />
              </div>

              {!statusState.success && statusState.error ? (
                <p
                  role="alert"
                  className="wireframe-border bg-white px-5 py-3 text-sm text-whart-urgent"
                >
                  {statusState.error}
                </p>
              ) : null}

              {statusState.success ? (
                <p
                  role="status"
                  className="wireframe-border bg-white px-5 py-3 text-sm text-emerald-700"
                >
                  {t("saveStatusSuccess")}
                </p>
              ) : null}

              <PlaceTaxonomyPanel
                placeId={place.id}
                taxonomy={taxonomy}
                artForms={artForms}
                artisticPeriods={artisticPeriods}
                culturalAreas={culturalAreas}
                exhibitionTypes={exhibitionTypes}
              />

              <PlaceOpeningHoursPanel
                placeId={place.id}
                googlePlaceId={place.google_place_id}
                initialOpeningHours={place.opening_hours_structured}
              />

              <ShareLinkButton shareUrl={shareUrl} />
            </div>

            <InternalNotesField
              entityId={place.id}
              value={internalNotes}
              disabled={isContentPending}
              isPending={isInternalNotesPending}
              saveSpinner={<SaveSpinner />}
              error={
                internalNotesState.success ? undefined : internalNotesState.error
              }
              success={internalNotesState.success}
              onChange={setInternalNotes}
              onBlur={handleInternalNotesBlur}
            />
          </aside>
        </div>
      )}

      <Dialog
        open={linkedEventsResyncCount != null}
        onClose={() => setLinkedEventsResyncCount(null)}
        title={t("linkedEventsResyncedTitle")}
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            {t("linkedEventsResynced", {
              count: linkedEventsResyncCount ?? 0,
            })}
          </p>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => setLinkedEventsResyncCount(null)}
            >
              {tCommon("close")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
